import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import HTMLContainer from 'components/common/HTMLContainer/HTMLContainer'
import ClientRendering from 'components/common/ClientRendering/ClientRendering'
import { A } from 'components/common/Link/Link'
import toggle from 'components/common/hoc/toggle'
import { getCleanText, truncate, flatten } from './helpers'

const getHTMLContainer = (content, ShowMoreButton) =>
  <Fragment>
    <HTMLContainer inline content={ content } />
    { ShowMoreButton }
  </Fragment>

const getComponentJsx = ({ Wrapper = HTMLContainer, ChildWrapper, data, attrs, ShowMoreButton }, index) =>
  <Wrapper key={ index } { ...attrs }>
    {
      !ChildWrapper ? getHTMLContainer(flatten([data]).join(' '), ShowMoreButton) :
        data.map((item, index) =>
          <ChildWrapper key={ index }>
            { getHTMLContainer(item, data.length - 1 === index && ShowMoreButton) }
          </ChildWrapper>
        )
    }
  </Wrapper>

getComponentJsx.propTypes = {
  attrs: PropTypes.shape({}),
  ChildWrapper: PropTypes.func,
  data: PropTypes.arrayOf(PropTypes.string),
  ShowMoreButton: PropTypes.func,
  Wrapper: PropTypes.shape({})
}

const createMapItemData = (data, limit, characterCount) => {
  let newData = ''
  let newCharacterCount = 0
  const cleanText = getCleanText(data)
  if (characterCount + cleanText.length > limit) {
    newData = truncate(data, limit - characterCount, '...')
    newCharacterCount = characterCount + (limit - characterCount)
  } else {
    newData = data
    newCharacterCount = characterCount + cleanText.length
  }
  return { data: newData, characterCount: newCharacterCount }
}

const getFullComponentJsx = (inputMap, limit, showAll, ShowMoreButton) => {
  let inputs = []
  let characterCount = 0
  if (showAll) {
    inputs = [...inputMap]
  } else {
    inputMap.forEach((item) => {
      if (characterCount < limit) {
        const data = flatten([item.data])
        const mapItem = {
          data: [],
          attrs: item.attrs || {},
          characterCount
        }
        data.forEach(childItem => {
          if ((mapItem.characterCount) < limit) {
            const childMapItem = createMapItemData(childItem, limit, mapItem.characterCount)
            mapItem.data.push(childMapItem.data)
            mapItem.characterCount = childMapItem.characterCount
          }
        })
        characterCount += mapItem.characterCount
        inputs.push({
          Wrapper: item.Wrapper,
          ChildWrapper: item.ChildWrapper,
          attrs: item.attrs,
          data: mapItem.data
        })
      }
    })
  }

  inputs[inputs.length - 1].ShowMoreButton = ShowMoreButton

  return inputs.map((mapItem, index) => getComponentJsx(mapItem, index))
}

const getCleanTextFullLength = (collection) =>
  (collection || []).reduce((oldValue, item) => {
    if (typeof (item.data || item) === 'string') {
      return oldValue + getCleanText((item.data || item)).length
    } else if (typeof item.data === 'object') {
      return oldValue + getCleanTextFullLength(item.data)
    }
    return 0
  }, 0)

const ReadMoreComponent = ({
  enableShowLess,
  expanded,
  href = '#',
  inputMap,
  limit,
  target = '_self',
  toggleHandlers
}) => {
  const attr = {}
  if (href === '#') {
    attr.prevent = true
    attr.handler = toggleHandlers.onClick
  }
  const showMoreLink = (!expanded || !!enableShowLess) && getCleanTextFullLength(inputMap) > limit
  return (
    <Fragment>
      {
        getFullComponentJsx(
          inputMap,
          limit,
          expanded,
          (
            showMoreLink &&
            <React.Fragment>
              &nbsp;&nbsp;
              <A
                data-at-readmore-link={ expanded ? 'less' : 'more' }
                bolder
                href={ href }
                target={ target }
                { ...attr } >
                Read { expanded ? 'Less' : 'More' }
              </A>
            </React.Fragment>
          )
        )
      }
    </Fragment>
  )
}

ReadMoreComponent.propTypes = {
  enableShowLess: PropTypes.bool,
  expanded: PropTypes.bool,
  href: PropTypes.string,
  inputMap: PropTypes.arrayOf(PropTypes.shape({
    ChildWrapper: PropTypes.shape({}),
    data: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string)
    ]),
    Wrapper: PropTypes.shape({})
  })),
  limit: PropTypes.number,
  target: PropTypes.string,
  toggleHandlers: PropTypes.shape({
    onClick: PropTypes.func
  })
}

const ReadMoreComplex = ({ ...props }) =>
  <ClientRendering>
    <ReadMoreComponent { ...props } />
  </ClientRendering>

export default toggle(ReadMoreComplex)
